// @ts-nocheck
/**
 * Autosize columns with double clicking headers' resize handle.
 *
 * Credits: https://github.com/naresh-n/slickgrid-column-data-autosize
 */
import debug from 'debug';
import * as _ from 'lodash';
import {
  SlickGrid,
  SlickPlugin,
  Column,
  Utils,
  HtmlElementPosition,
} from 'slickgrid';
import { assertNonNullish } from '../helpers';

const log = debug('itp:slick:plugins:AutosizeColumn');

export class AutosizeColumn<T> implements SlickPlugin {
  pluginName = 'AutosizeColumn' as const;

  private grid: SlickGrid;
  private unsubscribeAll: () => void;
  private canvasContext: CanvasRenderingContext2D;

  readonly maxWidth: number;

  constructor(maxWidth: number = 200) {
    this.maxWidth = maxWidth;
    const cvc = document.createElement('canvas').getContext('2d');
    if (cvc) {
      this.canvasContext = cvc;
    }
  }

  init(grid: SlickGrid): void {
    this.grid = grid;

    const resizeColumnHandler = this.onResizeColumn.bind(this);

    grid.getContainerNode().addEventListener('dblclick', resizeColumnHandler);

    this.unsubscribeAll = () =>
      grid
        .getContainerNode()
        .removeEventListener('dblclick', resizeColumnHandler);

    log('init completed. maxWidth=%s', this.maxWidth);
  }

  destroy(): void {
    this.unsubscribeAll();
  }

  resizeAllColumns() {
    const grid = this.grid;
    const columns = grid.getColumns();

    const cnode = Array.from(
      this.containerNode.getElementsByClassName('slick-header-column'),
    );

    cnode.forEach((el) => {
      const column = getColumnFromHeaderElement(grid, el);
      assertNonNullish(column.id, 'Column id must not be null.');

      const headerWidth = getElementWidth(el);

      if (!_.isFinite(headerWidth)) {
        console.warn(
          `Header width is not a number. columnId: ${column.id}, el: ${el}`,
        );
        return;
      }

      const colIndex = grid.getColumnIndex(column.id);
      const maxColumnTextWidth = this.getMaxColumnTextWidth(column, colIndex);

      const autoSizeWidth = Math.min(
        this.maxWidth,
        Math.max(headerWidth, maxColumnTextWidth) + 1,
      );

      if (!_.isFinite(autoSizeWidth)) {
        console.warn(
          `Autosize width is not a number. maxWidth: ${this.maxWidth}`,
        );
        return;
      }
      column.width = autoSizeWidth;

      log('%s width:=%s', column.id, autoSizeWidth);
    });

    grid.setColumns(columns);
    grid.onColumnsResized.notify();
  }

  private get containerNode() {
    return this.grid.getContainerNode();
  }

  private onResizeColumn(e: Event) {
    const target = e.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (!target.classList.contains('slick-resizable-handle')) {
      return;
    }

    const headerEl = target.closest('.slick-header-column');
    if (!headerEl) {
      return;
    }

    const columnDef = getColumnFromHeaderElement(this.grid, headerEl);
    if (!columnDef || !columnDef.resizable) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const grid = this.grid,
      headerWidth = getElementWidth(headerEl),
      colIndex = grid.getColumnIndex(columnDef.id),
      allColumns = grid.getColumns(),
      column = allColumns[colIndex],
      autoSizeWidth =
        Math.max(headerWidth, this.getMaxColumnTextWidth(column, colIndex)) + 1;

    if (autoSizeWidth !== column.width) {
      column.width = autoSizeWidth;
      grid.setColumns(allColumns);
      grid.onColumnsResized.notify();
    }
  }

  private getMaxColumnTextWidth<T>(column: Column<T>, colIndex: number) {
    const rowEl = this.createRow(column);

    let data = this.grid.getData();

    if (data && _.isFunction(data.getItems)) {
      data = data.getItems();
    }

    const texts = [];
    for (let i = 0; i < data.length; i++) {
      texts.push(data[i][column.field]);
    }

    const template = this.getMaxTextTemplate(
      texts,
      column,
      colIndex,
      data,
      rowEl,
    );
    const width = this.getTemplateWidth(rowEl, template);

    if (!_.isFinite(width)) {
      console.warn(`Template width is not a number. colIndex=${colIndex}`);
    }

    rowEl.remove();

    return width;
  }

  private getTemplateWidth(rowEl: HTMLElement, template: string) {
    for (const cell of rowEl.getElementsByClassName('slick-cell')) {
      const c = cell as HTMLElement;
      c.innerHTML = template;
      for (const node of c.children) {
        (node as HTMLElement).style.position = 'relative';
      }
      return c.offsetWidth + 1;
    }

    console.warn(`Row element has no children.`, rowEl);
  }

  private createRow(column: Column<T>) {
    const rowEl = Utils.createDomElement('div', { className: 'slick-row' });
    rowEl.appendChild(
      Utils.createDomElement('div', {
        className: 'slick-cell',
        style: {
          visibility: 'hidden',
          'text-overflow': 'initial',
          'white-space': 'nowrap',
        },
      }),
    );

    for (const el of this.grid
      .getContainerNode()
      .getElementsByClassName('grid-canvas')) {
      el.appendChild(rowEl);
      return rowEl;
    }

    console.warn('Could not append row element: grid-canvas not found.');
  }

  private deleteRow(rowEl) {
    rowEl.remove();
  }

  private getMaxTextTemplate(
    texts: string[],
    column: Column<T>,
    colIndex: number,
    data: T,
    rowEl: HTMLElement,
  ) {
    const formatter = column.formatter;

    let max = 0;
    let maxTemplate: string = null;

    _.each(texts, (text, index) => {
      let template;

      if (formatter) {
        template = Utils.createDomElement('span');
        template.innerHTML = formatter(
          index,
          colIndex,
          text,
          column,
          data[index],
          this.grid,
        );
        text = (template as HTMLElement).textContent || text;
      }

      const length = text
        ? getElementWidthUsingCanvas(this.canvasContext, rowEl, text)
        : 0;

      if (length > max) {
        max = length;
        maxTemplate = template ? (template as HTMLElement).innerText : text;
      }
    });

    return maxTemplate;
  }
}

function getElementWidth(element: HTMLElement) {
  const clone = element.cloneNode(true) as HTMLElement;

  clone.style.cssText =
    'position: absolute; visibility: hidden;right: auto;text-overflow: initial;white-space: nowrap;';
  element.parentNode.insertBefore(clone, element);

  const width = clone.offsetWidth;

  clone.parentNode.removeChild(clone);
  return width;
}

function getElementWidthUsingCanvas(
  context: CanvasRenderingContext2D,
  element: HTMLElement,
  text: string,
) {
  const compStyles = getComputedStyle(element);
  const fontSize = compStyles.getPropertyValue('font-size');
  const fontFamily = compStyles.getPropertyValue('font-family');

  context.font = fontSize + ' ' + fontFamily;

  return context.measureText(text).width;
}

function getColumnFromHeaderElement<T>(grid: SlickGrid, el: HTMLElement) {
  let result = el.dataset['column'] as Column<T>;

  const columnId = el.dataset.columnId || el.dataset.id;

  if (!result) {
    result = _.find(grid.getColumns(), { id: columnId });
  }

  if (!result) {
    console.warn(`Column instance not found for element`, el);
  }

  return result;
}
